import React from "react"
import { useTranslation } from "react-i18next"

import * as UserApi from "../../api/UserApi"
import * as BikeApi from "features/bike/api/BikeApi"

import { User, UserData, ErrorResponse } from "models"
import { useForm, useAlert, useOperator } from "hooks"
import { formStatusEnum } from "components/Form/formUtils"
import {
	ACCEPTED_NOTIFICATION,
	checkIsCompanyConsumer,
	checkIsPrivateConsumer,
	compareIntersectionEgalityOf2Objects,
	IS_FNUCI_SYNC,
	jsonHash,
	PRIVATE_GENDER_VALUE,
} from "utils"

import { Spinner, Button } from "components/Elements"
import { FormElement } from "components/Form"

type UserInfoPropsProps = {
	userData: Partial<User & UserData>
	refreshUser: () => Promise<void>
}

const FNUCI_PRIVATE_FIELDS_LIST = ["last_name", "first_name", "phone"]
const FNUCI_COMPANY_FIELDS_LIST = ["social_reason", "phone"]

export const UserInfo: React.FC<UserInfoPropsProps> = ({ userData, refreshUser }) => {
	const { currentOperator, isDecathlon, isBicycode, isMFC } = useOperator()
	const {
		formFields,
		formStatus,
		setFormStatus,
		handleChange,
		errors,
		showLocalErrors,
		setShowLocalErrors,
		handleValidation,
		discardChanges,
	} = useForm({
		defaultFormFields: userData,
		defaultFormStatus: formStatusEnum.readonly,
	})

	const { t } = useTranslation()
	const { alert } = useAlert()

	const isLoading = formStatus === formStatusEnum.loading
	const isReadonly = formStatus === formStatusEnum.readonly
	const isReady = formStatus === formStatusEnum.ready

	const isCheckboxForOcodeInfoDisplayed = isBicycode
	const isCheckboxForNewsletterDisplayed = !isDecathlon && !isMFC

	const isPrivateUser = checkIsPrivateConsumer({
		gender: formFields?.description?.gender,
	})
	const isCompany = checkIsCompanyConsumer({
		gender: formFields?.description?.gender,
	})

	const formHasChanged = jsonHash(userData) !== jsonHash(formFields)

	const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
		event.preventDefault()
		// Validate inputs
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			try {
				// hide error
				setShowLocalErrors(false)
				// prevent form to be submitted again
				setFormStatus(formStatusEnum.loading)

				const userDescription = formFields.description

				// 1 ----------------- BIKE UPDATE - FNUCI --------------------------------
				// We compare userData.description and formFields.description
				// If differences, we update all bikes with the new bikeOwner's information
				// ------------------------------------------------------------------------
				const isOwnerDifferent = compareIntersectionEgalityOf2Objects(
					userData.description,
					userDescription,
					userData.description.gender === PRIVATE_GENDER_VALUE
						? FNUCI_PRIVATE_FIELDS_LIST
						: FNUCI_COMPANY_FIELDS_LIST
				)

				if (isOwnerDifferent) {
					// First, we fetch bikes
					// ---------------------
					const { res: fetchedBikes, getError: getBikesError } = await BikeApi.getBikes()
					if (getBikesError) throw getBikesError()
					if (!fetchedBikes) throw getBikesError()

					// Then, we exclude all bikes who need an "action" :
					// - bikes displayed in Notifications' area
					// - Or bikes not sync with FNUCI
					// -------------------------------------------------
					const filteredBikes = fetchedBikes.filter(
						(bike) =>
							!bike.bike_action_needed && bike.is_sync_with_fnuci === IS_FNUCI_SYNC
					)

					// Finally, we update each bike
					// -------------------
					// Here, gender and email are not updated but we need to send them
					const bikeOwner = {
						gender: userData.description.gender,
						mail: userData.description.mail,
						phone: userDescription.phone.trim(),
						...(() => {
							if (userDescription.gender === PRIVATE_GENDER_VALUE) {
								return {
									first_name: userDescription.first_name.trim(),
									last_name: userDescription.last_name.trim(),
								}
							} else {
								return {
									social_reason: userDescription.social_reason.trim(),
								}
							}
						})(),
					}

					for (const bike of filteredBikes) {
						const updatedBikePayload = {
							bike_id: bike.bike_id,
							row_version: bike.row_version,
							bike_owner: bikeOwner,
						}

						const { getError: getUpdateBikeError } = await BikeApi.updateBike(
							updatedBikePayload
						)
						if (getUpdateBikeError()) throw getUpdateBikeError()
					}
				}

				// 2 ----------------- PROFIL UPDATE -------------------------
				// -----------------------------------------------------------

				const updateUserDescription = {
					...userDescription,
					phone: userDescription.phone.trim(),
					...(() => {
						if (userDescription.gender === PRIVATE_GENDER_VALUE) {
							return {
								first_name: userDescription.first_name.trim(),
								last_name: userDescription.last_name.trim(),
							}
						} else {
							return {
								social_reason: userDescription.social_reason.trim(),
							}
						}
					})(),
				}

				const updateUserData = {
					account_id: formFields.account_id,
					row_version: formFields.row_version,
					description: updateUserDescription,
					...(() => {
						if (formFields.notifications.operator_notifications_accepted_at) {
							return {
								operator_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
					...(() => {
						if (formFields.notifications.ocode_notifications_accepted_at) {
							return {
								ocode_notifications_accepted: ACCEPTED_NOTIFICATION,
							}
						}
					})(),
				}

				const { getError } = await UserApi.updateUser(updateUserData)
				if (getError()) throw getError()

				alert("success", t("info_update", { ns: "form" }))
				await refreshUser()
			} catch (error) {
				console.error(error)
				const errorMsg = (error as ErrorResponse)?.message?.toString()
				alert("error", errorMsg)
			} finally {
				setFormStatus(formStatusEnum.ready)
			}
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleChange,
	}

	return (
		<div className="c-block px-4 lg:px-8 py-6 md:py-7">
			<h2 className="c-block__title text-2xl -top-6">
				{t("my_information", { ns: "user" })}
			</h2>

			{formFields && (
				<form>
					{isPrivateUser && (
						<div className="flex justify-between flex-wrap mb-1 md:mb-4">
							{/* Prénom */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.description.first_name}
								name="description.first_name"
								type="text"
								label={t("firstname", { ns: "user" })}
								className="md:mt-6 w-full lg:w-2/4 px-1"
								readonly={isReadonly}
								required={true}
							/>

							{/* Nom */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.description.last_name}
								name="description.last_name"
								type="text"
								label={t("lastname", { ns: "user" })}
								className="mt-1 md:mt-6 w-full lg:w-2/4 px-1"
								readonly={isReadonly}
								required={true}
							/>
						</div>
					)}

					{isCompany && (
						<>
							{/* Raison sociale */}
							<FormElement
								{...formElementCommonProps}
								value={formFields.description.social_reason}
								name="description.social_reason"
								type="text"
								label={t("raison_sociale", {
									ns: "user",
								})}
								className="mt-1 md:mt-6 px-1"
								readonly={isReadonly}
								required={true}
							/>
						</>
					)}

					{/* Téléphone */}
					<FormElement
						{...formElementCommonProps}
						value={formFields.description.phone}
						name="description.phone"
						type="phone"
						label={t("phone", { ns: "user" })}
						className="my-1 mb-4 px-1"
						disabled={isReadonly}
						required={true}
					/>

					{/* newsletter */}
					<div className="px-3">
						{isCheckboxForNewsletterDisplayed && (
							<>
								<h3 className="font-bold mt-8 mb-0 text-primary">
									{t("communications", { ns: "user" })}
								</h3>

								<FormElement
									{...formElementCommonProps}
									value={
										formFields.notifications.operator_notifications_accepted_at
									}
									name="notifications.operator_notifications_accepted_at"
									type="checkbox"
									checkboxLabel={t("label_newsletter", {
										ns: `auth_${currentOperator.folderName}`,
									})}
									className="text-xxs"
									disabled={isReadonly}
								/>
							</>
						)}

						{isCheckboxForOcodeInfoDisplayed && (
							<FormElement
								{...formElementCommonProps}
								value={formFields.notifications.ocode_notifications_accepted_at}
								name="notifications.ocode_notifications_accepted_at"
								type="checkbox"
								checkboxLabel={t("ocode_newsletter", {
									ns: `auth_${currentOperator.folderName}`,
								})}
								className="mb-5 text-xxs"
								disabled={isReadonly}
							/>
						)}
					</div>

					{isReadonly && (
						<Button
							variant="primary"
							type="submit"
							onClick={() => setFormStatus(formStatusEnum.ready)}
							className="px-10 py-1 mt-10"
							size="small"
						>
							{t("global.edit")}
						</Button>
					)}

					{isReady && (
						<div className="flex items-center mt-10 justify-center md:justify-start">
							<Button
								variant="secondary"
								type="submit"
								onClick={handleSubmit}
								className="mx-1 px-10 py-1 lg:mx-3"
								disabled={!formHasChanged}
								size="small"
							>
								{t("global.validate")}
							</Button>
							<Button
								variant="secondaryBordered"
								type="button"
								onClick={() =>
									discardChanges({ formHasChanged, refreshData: refreshUser })
								}
								className="mx-1 px-10 py-1 lg:mx-3"
								size="small"
							>
								{t("global.cancel")}
							</Button>
						</div>
					)}

					{isLoading && (
						<div className="flex justify-center my-8">
							<Spinner color={"var(--primaryColor)"} size={"100px"} />
						</div>
					)}
				</form>
			)}
		</div>
	)
}
